if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.isLightbox = this.hasAttribute('data-zaoud-lightbox');
        if (!this.isLightbox) return;

        this.addEventListener(
          'pointerup',
          (event) => {
            if (
              event.target.closest(
                '.zaoud-lightbox__nav, .zaoud-lightbox__bar, .zaoud-lightbox__thumbs, .zaoud-lightbox__content, .product-media-modal__toggle'
              )
            ) {
              event.stopImmediatePropagation();
            }
          },
          true
        );

        this.addEventListener('keyup', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.code === 'ArrowRight') this.stepLightbox(1);
          if (event.code === 'ArrowLeft') this.stepLightbox(-1);
        });
      }

      connectedCallback() {
        super.connectedCallback();
        if (!this.isLightbox || this.lightboxBound) return;
        this.lightboxBound = true;
        this.bindLightboxControls();
      }

      bindLightboxControls() {
        this.lightboxPrev = this.querySelector('.zaoud-lightbox__nav--prev');
        this.lightboxNext = this.querySelector('.zaoud-lightbox__nav--next');
        this.lightboxContent = this.querySelector('.zaoud-lightbox__content');
        this.lightboxPrev?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepLightbox(-1);
        });
        this.lightboxNext?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepLightbox(1);
        });
        this.querySelectorAll('.zaoud-lightbox__thumb').forEach((thumb) => {
          thumb.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.activateLightboxMedia(thumb.getAttribute('data-media-id'));
          });
        });
        this.lightboxContent?.addEventListener('click', (event) => this.toggleLightboxZoom(event));
        this.lightboxContent?.addEventListener('mousemove', (event) => this.panLightboxZoom(event));
      }

      cacheLightboxElements() {
        this.lightboxThumbs = Array.from(this.querySelectorAll('.zaoud-lightbox__thumb'));
        this.lightboxItems = this.lightboxThumbs
          .map((thumb) =>
            this.querySelector(`.zaoud-lightbox__content [data-media-id="${thumb.getAttribute('data-media-id')}"]`)
          )
          .filter(Boolean);
        if (!this.lightboxItems.length) {
          this.lightboxItems = Array.from(this.querySelectorAll('.zaoud-lightbox__content [data-media-id]'));
        }
        this.lightboxCount = this.querySelector('[data-zaoud-lightbox-count]');
      }

      hide() {
        this.resetLightboxZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const openerId = this.openedBy?.getAttribute('data-media-id');
        if (this.isLightbox) {
          this.cacheLightboxElements();
          this.activateLightboxMedia(openerId);
          return;
        }

        this.querySelectorAll(`[data-media-id]:not([data-media-id="${openerId}"])`).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${openerId}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      activateLightboxMedia(mediaId) {
        if (!this.lightboxItems?.length) return;

        const fallbackId = this.lightboxItems[0].getAttribute('data-media-id');
        const targetId = mediaId && this.lightboxItems.some((el) => el.getAttribute('data-media-id') === mediaId)
          ? mediaId
          : fallbackId;

        this.lightboxItems.forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === targetId);
        });
        this.lightboxThumbs.forEach((thumb) => {
          if (thumb.getAttribute('data-media-id') === targetId) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        const activeMedia = this.lightboxItems.find((element) => element.getAttribute('data-media-id') === targetId);
        if (!activeMedia) return;

        const index = this.lightboxItems.indexOf(activeMedia);
        if (this.lightboxCount) {
          this.lightboxCount.textContent = `${index + 1} / ${this.lightboxItems.length}`;
        }

        this.syncLightboxNav(index);
        this.resetLightboxZoom();

        const activeThumb = this.lightboxThumbs[index];
        const thumbs = activeThumb?.closest('.zaoud-lightbox__thumbs');
        const thumbItem = activeThumb?.closest('li') || activeThumb;
        if (thumbs && thumbItem) {
          const thumbsRect = thumbs.getBoundingClientRect();
          const itemRect = thumbItem.getBoundingClientRect();
          thumbs.scrollTo({
            left: thumbs.scrollLeft + itemRect.left - thumbsRect.left - (thumbsRect.width - itemRect.width) / 2,
            behavior: 'smooth',
          });
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      toggleLightboxZoom(event) {
        const activeMedia = this.lightboxItems?.find((element) => element.classList.contains('active'));
        if (!activeMedia || activeMedia.nodeName !== 'IMG') return;
        if (!activeMedia.contains(event.target) && event.target !== activeMedia) return;

        if (this.lightboxContent?.classList.contains('is-zoomed')) {
          this.resetLightboxZoom();
          return;
        }

        this.lightboxContent.classList.add('is-zoomed');
        this.setLightboxZoomOrigin(event);
      }

      panLightboxZoom(event) {
        if (!this.lightboxContent?.classList.contains('is-zoomed')) return;
        this.setLightboxZoomOrigin(event);
      }

      setLightboxZoomOrigin(event) {
        const rect = this.lightboxContent.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
        this.lightboxContent.style.setProperty('--zoom-x', `${x}%`);
        this.lightboxContent.style.setProperty('--zoom-y', `${y}%`);
      }

      resetLightboxZoom() {
        this.lightboxContent?.classList.remove('is-zoomed');
        this.lightboxContent?.style.removeProperty('--zoom-x');
        this.lightboxContent?.style.removeProperty('--zoom-y');
      }

      syncLightboxNav(index) {
        const last = (this.lightboxItems?.length || 1) - 1;
        const prev = this.lightboxPrev || this.querySelector('.zaoud-lightbox__nav--prev');
        const next = this.lightboxNext || this.querySelector('.zaoud-lightbox__nav--next');
        if (prev) {
          prev.toggleAttribute('hidden', index <= 0);
          prev.toggleAttribute('disabled', index <= 0);
        }
        if (next) {
          next.toggleAttribute('hidden', index >= last);
          next.toggleAttribute('disabled', index >= last);
        }
      }

      stepLightbox(direction) {
        if (!this.lightboxItems?.length) return;
        const currentIndex = this.lightboxItems.findIndex((element) => element.classList.contains('active'));
        const startIndex = currentIndex < 0 ? 0 : currentIndex;
        const nextIndex = startIndex + direction;
        if (nextIndex < 0 || nextIndex >= this.lightboxItems.length) return;
        this.activateLightboxMedia(this.lightboxItems[nextIndex].getAttribute('data-media-id'));
      }
    }
  );
}
